import { Fragment } from 'react';
import { Menu, MenuButton, MenuItems, Transition } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead, INotification } from '@/shared/hooks/useNotifications';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface INotificationsDropdownProps {
  className?: string;
}

/**
 * Notifications dropdown component
 * Shows unread count badge and dropdown with notification list
 */
export function NotificationsDropdown({ className }: INotificationsDropdownProps) {
  const { data, isLoading } = useNotifications({ unreadOnly: false, page: 1, pageSize: 20 });
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();

  const notifications = data?.data.notifications ?? [];
  const unreadCount = data?.data.summary.unread ?? 0;

  const handleNotificationClick = async (notification: INotification) => {
    if (!notification.isRead) {
      await markReadMutation.mutateAsync(notification.id);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllReadMutation.mutateAsync();
  };

  const getNotificationIcon = (_type: string) => {
    // You can customize icons based on notification type
    return '🔔';
  };

  const getNotificationColor = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'NORMAL':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'LOW':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Menu as="div" className={clsx('relative', className)} data-testid="notifications-dropdown">
      <MenuButton
        className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        aria-label="View notifications"
        data-testid="notifications-button"
      >
        <BellIcon className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-medium text-white bg-red-500 rounded-full" data-testid="notifications-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </MenuButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <MenuItems className="absolute right-0 z-50 mt-2 w-96 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-[32rem] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  void handleMarkAllRead();
                }}
                disabled={markAllReadMutation.isPending}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
              >
                {markAllReadMutation.isPending ? 'Marking...' : 'Mark all read'}
              </button>
            )}
          </div>

          {/* Notifications list */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner size="sm" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <BellIcon className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-900">No notifications</p>
                <p className="text-xs text-gray-500 mt-1">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={clsx(
                      'p-4 hover:bg-gray-50 transition-colors cursor-pointer',
                      !notification.isRead && 'bg-primary-50/50'
                    )}
                    onClick={() => {
                      void handleNotificationClick(notification);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={clsx(
                          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs border',
                          getNotificationColor(notification.priority)
                        )}
                      >
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p
                            className={clsx(
                              'text-sm font-medium',
                              notification.isRead ? 'text-gray-700' : 'text-gray-900'
                            )}
                          >
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-1.5" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{notification.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-500">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                          </span>
                          {notification.linkUrl && (
                            <Link
                              to={notification.linkUrl}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            >
                              View →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </MenuItems>
      </Transition>
    </Menu>
  );
}

